import { t } from '../../../shared/config/translations'
import type {
	ClientRelease,
	ReleaseHistory,
} from '../../../shared/types/collection'
import { updateHistory } from '../../api/history'
import { toIsoDateString } from '../../utils/format'
import initDialog from '../Dialog'
import { applyReleaseHistoryUpdate } from './ReleaseDetails'
import { releaseEditDialogContentTemplate } from './releaseEditDialogTemplates'
import {
	type EditHistoryEntry,
	releaseEditHistoryListTemplate,
} from './releaseEditTemplates'

type EditDialogConfig = {
	content: string
	bind: () => void
	onConfirm: (formData?: FormData | null) => Promise<void>
}

const HIDE_DURATION_MONTH_MS = 30 * 24 * 60 * 60 * 1000

function buildReleaseEditDialog(release: ClientRelease): EditDialogConfig {
	const releaseId = release.data?.id ?? release.id
	const releaseEl = document.getElementById('release')
	const releaseInstanceId = releaseEl?.getAttribute(
		'data-release-instance-id',
	)

	const initialHistoryDates = (release.history?.historyDates ?? []).map(
		toIsoDateString,
	)
	const entries: EditHistoryEntry[] = initialHistoryDates.map((date, id) => ({
		id,
		date,
		isNew: false,
		removed: false,
	}))
	for (let i = 0; i < (release.history?.unknownDateCount ?? 0); i++) {
		entries.push({
			id: entries.length,
			date: null,
			isNew: false,
			removed: false,
		})
	}
	let nextEntryId = entries.length
	let initialHideDurationMonths: number | 'forever' | null = null
	if (release.history?.isHidden && !release.history.hiddenUntil) {
		initialHideDurationMonths = 'forever'
	} else if (release.history?.hiddenUntil) {
		const diffMs =
			new Date(release.history.hiddenUntil).getTime() - Date.now()
		if (diffMs > 0) {
			const months = Math.round(diffMs / HIDE_DURATION_MONTH_MS)
			initialHideDurationMonths = Math.max(1, Math.min(12, months))
		}
	}
	const bind = () => {
		const root = document.getElementById('release-edit-dialog')
		const addDateInput =
			root?.querySelector<HTMLInputElement>('#edit-add-date')
		const addDateBtn =
			root?.querySelector<HTMLButtonElement>('#edit-add-date-btn')
		const list = root?.querySelector<HTMLUListElement>('.edit-history-list')
		const empty = root?.querySelector<HTMLElement>('.edit-history-empty')
		const removeAll =
			root?.querySelector<HTMLButtonElement>('#edit-remove-all')

		const syncAddButton = () => {
			if (addDateBtn) addDateBtn.disabled = !addDateInput?.value
		}
		const render = () => {
			if (list) list.innerHTML = releaseEditHistoryListTemplate(entries)
			if (empty) empty.hidden = entries.length > 0
			if (removeAll)
				removeAll.disabled = !entries.some((entry) => !entry.removed)
			syncAddButton()
		}
		render()
		addDateInput?.addEventListener('input', syncAddButton)
		addDateBtn?.addEventListener('click', () => {
			if (!addDateInput?.value || !addDateInput.reportValidity()) return
			entries.push({
				id: nextEntryId++,
				date: toIsoDateString(addDateInput.value),
				isNew: true,
				removed: false,
			})
			addDateInput.value = ''
			render()
			addDateInput.focus()
		})
		list?.addEventListener('click', (event) => {
			const button = (event.target as Element).closest<HTMLButtonElement>(
				'[data-history-entry]',
			)
			if (!button) return
			const entry = entries.find(
				(item) => item.id === Number(button.dataset.historyEntry),
			)
			if (!entry) return
			entry.removed = !entry.removed
			render()
			list.querySelector<HTMLButtonElement>(
				`[data-history-entry="${entry.id}"]`,
			)?.focus({ preventScroll: true })
		})
		removeAll?.addEventListener('click', () => {
			for (const entry of entries) entry.removed = true
			render()
			list?.querySelector<HTMLButtonElement>(
				'[data-history-entry]',
			)?.focus({ preventScroll: true })
		})
	}

	const onConfirm = async (formData?: FormData | null) => {
		const hideDurationValue =
			(formData?.get('edit-hide-duration') as string) ?? ''
		const hideDurationMonths =
			hideDurationValue === 'forever'
				? -1
				: hideDurationValue
					? Number(hideDurationValue)
					: null

		const remainingEntries = entries.filter((entry) => !entry.removed)
		const nextHistoryDates = remainingEntries.flatMap((entry) =>
			entry.date ? [entry.date] : [],
		)
		const draftUnknownDateCount = remainingEntries.filter(
			(entry) => entry.date === null,
		).length

		const updatedHistoryDoc = await updateHistory(
			releaseId,
			nextHistoryDates,
			hideDurationMonths,
			draftUnknownDateCount,
		)

		const updatedHistory: ReleaseHistory = {
			...(release.history ?? ({} as ReleaseHistory)),
			historyDates: (updatedHistoryDoc?.historyDates ?? []).map(
				(d: string | Date) => new Date(d),
			),
			hiddenUntil: updatedHistoryDoc?.hiddenUntil
				? new Date(updatedHistoryDoc.hiddenUntil)
				: undefined,
			isHidden: updatedHistoryDoc?.isHidden ?? false,
			listenCount:
				typeof updatedHistoryDoc?.listenCount === 'number'
					? updatedHistoryDoc.listenCount
					: nextHistoryDates.length + draftUnknownDateCount,
			unknownDateCount:
				updatedHistoryDoc?.unknownDateCount ?? draftUnknownDateCount,
		}

		applyReleaseHistoryUpdate(
			release,
			updatedHistory,
			releaseInstanceId ?? releaseId,
		)
	}

	return {
		content: releaseEditDialogContentTemplate(initialHideDurationMonths),
		bind,
		onConfirm,
	}
}

export async function handleEditButtonClick(
	release: ClientRelease,
): Promise<void> {
	const dialog = buildReleaseEditDialog(release)
	initDialog({
		id: 'release-edit-dialog',
		cancelText: t('dialog.cancel'),
		confirmText: t('dialog.save'),
		content: dialog.content,
		onConfirm: dialog.onConfirm,
		title: t('release.editTitle'),
	})
	dialog.bind()
}
